import React, { useState, useMemo, useCallback, useEffect } from "react";
import * as R from "ramda";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, Route } from "react-router-dom";
import { dispatchOnMount } from "@worcket/common/hocs";
import { Button, Tooltip, notification, Breadcrumb } from "antd";
import styled from "styled-components";

import ActionsSnackbar, {
  SELECT_ALL_STATUS
} from "../../components/ActionsSnackbar/ActionsSnackbar";
import {
  shareCandidate,
  multipleSelectCandidate,
  resetMultipleSelectCandidates,
  SELECT_CANDIDATES,
  SHARE_CANDIDATE,
  fetchCandidatesBase,
  FETCH_CANDIDATES_BASE,
  SELECT_CANDIDATE,
  candidatesBatchShare
} from "../../store/actions/candidates";
import SourceFilter from "../Opportunity/routes/OpportunityCandidates/filters/SourceFilter";
import SearchFilter from "../Opportunity/routes/OpportunityCandidates/filters/SearchFilter";
import ReferralFilter from "../Opportunity/routes/OpportunityCandidates/filters/ReferralFilter";
import ExperienceFilters from "../Opportunity/routes/OpportunityCandidates/filters/ExperienceFilters";
import Page from "../../components/display/Page";
import CheckboxFilters from "../Opportunity/routes/OpportunityCandidates/filters/CheckboxFilter";
import {
  FETCH_PROFESSIONS,
  FETCH_SKILLS,
  FETCH_STUDIES
} from "../../store/actions/catalogs";
import AgeFilters from "../Opportunity/routes/OpportunityCandidates/filters/AgeFilters";
import AddressFilter from "../Opportunity/routes/OpportunityCandidates/filters/AddressFilter";
import {
  EDUCATION_LEVELS,
  BATCH_ACTIONS,
  FILTERS_FOR
} from "../../constants/opportunity";
import {
  Header,
  Sidebar,
  Content,
  SearchContainer,
  Grid
} from "../Opportunity/routes/OpportunityCandidates/filters/FilterComponents";
import CandidateCard from "../../components/CandidateCard";
import { DESELECT_OPPORTUNITY } from "../../store/actions/opportunity";
import ShareCandidatesModal from "../Opportunity/routes/OpportunityCandidates/ShareCandidatesModal";
import { WrapperAction } from "../../components/CandidateModal/WrapperAction";
import { pluralsForList } from "../../utils/strings";
import { isInitialLoading, isLoading } from "../../utils/actions";
import useFilters, {
  DEFAULT_SERVER_FILTERS_FOR_CANDIDATES_BASE
} from "../Opportunity/routes/OpportunityCandidates/filters/useFilters";
import SkeletonCandidates from "../Opportunity/routes/OpportunityCandidates/Skeleton";
import NonResults from "../../components/transforms/filters/NonResults";
import {
  getFiltersForList,
  CANDIDATES_PER_PAGE
} from "../../utils/candidatesFilters";
import { useCandidatesData } from "../../hooks/custom/useCandidatesData";
import CustomPagination from "../../components/transforms/CustomPagination";
import { usePaginationLogic } from "../../hooks/custom/usePaginationLogic";
import * as gtag from "../../lib/gtag";
// import { getLanguages } from "../../utils/data/static/languages";
import { ACCEPT_LANGUAGE } from "../../locales/acceptedLanguages";

import CandidatesBaseDetails from "./CandidatesBaseDetails";

const sortOptions = R.sortBy(
  R.compose(
    R.toLower,
    R.prop("label")
  )
);

const Breadcrumbs = styled.div`
  margin: 20px;
  margin-top: 25px;

  .ant-breadcrumb {
    font-size: 12px !important;
    color: ${({ theme }) => theme["@gray-60"]};
  }
`;

const filtersUsed = FILTERS_FOR.base;

const filterKey = `candidate_base`;

function Wrapper({
  children,
  filters,
  filteredCandidates,
  handleFiltersChange,
  mappedMetadata
}) {
  return mappedMetadata > CANDIDATES_PER_PAGE ? (
    <CustomPagination
      page={filters.page}
      quantity={Math.ceil(mappedMetadata / CANDIDATES_PER_PAGE)}
      onChange={handleFiltersChange("page")}
    >
      {children({ data: filteredCandidates })}
    </CustomPagination>
  ) : (
    children({ data: filteredCandidates })
  );
}

const CandidatesBase = () => {
  const history = useHistory();
  const { t, i18n } = useTranslation();
  const locale = ACCEPT_LANGUAGE[i18n.language] || ACCEPT_LANGUAGE.DEFAULT;
  const { candidates, metadata: candidatesMetadata = false } = useSelector(
    state => state.candidates.candidatesBase
  );
  const { name } = useSelector(({ company }) => company);
  const { name: headquarter } = useSelector(({ headquarter }) => headquarter);
  const loading = useSelector(state =>
    isInitialLoading(FETCH_CANDIDATES_BASE, state)
  );

  const [shareCandidateModalVisible, setShareCandidateModalVisible] = useState(
    false
  );
  const [selectedCandidate, setSelectedCandidate] = useState({});
  const dispatch = useDispatch();

  const fieldsOfStudyList = useSelector(
    state => FETCH_STUDIES.getResponse(state) || []
  );

  const professionList = useSelector(
    state => FETCH_PROFESSIONS.getResponse(state) || []
  );

  const skillList = useSelector(state => FETCH_SKILLS.getResponse(state) || []);

  const multipleSelectedCandidates = useSelector(
    state => state.candidates.multipleSelectedCandidates
  );

  const sharingCandidate = useSelector(state =>
    isLoading(SHARE_CANDIDATE, state)
  );

  // const languageOptions = R.pipe(
  //   getLanguages,
  //   R.toPairs,
  //   R.map(([value, label]) => ({ value, label })),
  //   sortOptions
  // )();

  const educationOptions = EDUCATION_LEVELS.map(level => ({
    value: level,
    label: t(`catalogs:educationLevelsAdvanced.${level}`)
  }));

  const fieldsOfStudyOptions = useMemo(
    () =>
      R.pipe(
        R.map(item => ({
          label: item[locale] || item.field,
          value: item.field
        })),
        sortOptions
      )(fieldsOfStudyList),
    [fieldsOfStudyList, locale]
  );

  const skillOptions = useMemo(
    () =>
      R.pipe(
        R.map(item => ({
          label: item[locale] || item.skill,
          value: item.skill
        })),
        sortOptions
      )(skillList),
    [skillList, locale]
  );

  const professionOptions = useMemo(
    () =>
      R.pipe(
        R.map(item => ({
          label: item[locale] || item.label,
          value: item.id
        })),
        sortOptions
      )(professionList),
    [locale, professionList]
  );

  const snackbarActions = useMemo(
    () => [
      {
        "data-test": "multiple-candidates-send-invite-to-opp",
        description: t(
          "opportunity:candidates.multipleSelection.shareCandidateToOpp"
        ),
        icon: { type: sharingCandidate ? "loading" : "export" },
        onClick: () => {
          gtag.event({
            category: "Bulk",
            action: `CandidatesBase.Bulk.shareCandidateToOpp`
          });
          setShareCandidateModalVisible(true);
        },
        enabled: !sharingCandidate
      }
    ],
    [sharingCandidate, t]
  );

  const handleCandidateShare = useCallback(
    opportunities =>
      dispatch(
        shareCandidate({
          candidate: selectedCandidate,
          opportunities
        })
      ),
    [dispatch, selectedCandidate]
  );

  const handleSelectAllChange = useCallback(
    e =>
      dispatch(
        SELECT_CANDIDATES.run({
          isChecked: e.target.checked,
          candidates
        })
      ),
    [candidates, dispatch]
  );

  const onServerFiltersChange = useCallback(
    ({ filters }) => dispatch(fetchCandidatesBase({ filters })),
    [dispatch]
  );

  const {
    filteredCandidates,
    filters,
    handleFiltersChange,
    isEmpty
  } = useFilters({
    filterKey,
    filtersUsed,
    candidates,
    onServerFiltersChange,
    resetMultipleSelectCandidates,
    serverFiltersList: DEFAULT_SERVER_FILTERS_FOR_CANDIDATES_BASE
  });

  const { metadata, mappedMetadata } = useCandidatesData({
    candidates,
    metadata: candidatesMetadata,
    filters
  });

  const selectAllStatus = useMemo(
    () =>
      multipleSelectedCandidates.length >= 1 &&
      multipleSelectedCandidates.length === candidates.length
        ? SELECT_ALL_STATUS.CHECKED
        : multipleSelectedCandidates.length >= 1
        ? SELECT_ALL_STATUS.INDETERMINATE
        : SELECT_ALL_STATUS.UNCHECKED,
    [candidates.length, multipleSelectedCandidates.length]
  );

  useEffect(() => {
    dispatch(
      fetchCandidatesBase({
        filters: getFiltersForList({
          filtersUsed,
          filterKey,
          onlyServerFilters: true
        })
      })
    );
  }, [dispatch]);

  useEffect(() => {
    return () => dispatch(resetMultipleSelectCandidates());
  }, [dispatch]);

  // TODO FIX i18n interpolations to use the <Trans> Component instead of this hack
  const plurals = useMemo(() => pluralsForList(multipleSelectedCandidates), [
    multipleSelectedCandidates
  ]);

  const handleBatchShare = useCallback(
    opportunities =>
      dispatch(
        candidatesBatchShare({
          action: BATCH_ACTIONS.base.share,
          opportunities,
          candidates: multipleSelectedCandidates
        })
      ),
    [dispatch, multipleSelectedCandidates]
  );

  usePaginationLogic({
    candidates,
    filters,
    filtersUsed,
    handleFiltersChange,
    loading,
    mappedMetadata
  });

  return (
    <Page>
      <Breadcrumbs>
        <Breadcrumb separator=">" data-test="breadcrum">
          {headquarter && (
            <Breadcrumb.Item href="/compañias">{headquarter}</Breadcrumb.Item>
          )}
          <Breadcrumb.Item href="/oportunidades">{name}</Breadcrumb.Item>
          <Breadcrumb.Item>{t("breadcrumbs:candidatesBase")}</Breadcrumb.Item>
        </Breadcrumb>
      </Breadcrumbs>
      <Header isBase>
        <SourceFilter
          loading={loading}
          metadata={metadata}
          onChange={handleFiltersChange("registered")}
          value={filters.registered}
        />

        <SearchContainer>
          <SearchFilter
            searchText={filters.searchText}
            setSearchText={handleFiltersChange("searchText")}
            isDisabled={loading}
          />
        </SearchContainer>
      </Header>
      <Content>
        <Sidebar>
          <CheckboxFilters
            maxLength={4}
            loading={loading}
            title={t("opportunity:candidates.fieldsStudyFilters.title")}
            setValues={handleFiltersChange("fieldsofstudy")}
            values={filters.fieldsofstudy || []}
            options={fieldsOfStudyOptions}
            viewMoreLabels={t(
              "opportunity:candidates.fieldsStudyFilters.viewMore"
            )}
            sorted={true}
            grouped={true}
            placeholder={t(
              "opportunity:candidates.fieldsStudyFilters.placeholder"
            )}
          />
          <CheckboxFilters
            maxLength={4}
            loading={loading}
            title={t("opportunity:candidates.professionFilters.title")}
            setValues={handleFiltersChange("professions")}
            values={filters.professions || []}
            options={professionOptions}
            viewMoreLabels={t(
              "opportunity:candidates.professionFilters.viewMore"
            )}
            sorted={true}
            grouped={true}
            placeholder={t(
              "opportunity:candidates.professionFilters.placeholder"
            )}
          />
          <CheckboxFilters
            maxLength={4}
            loading={loading}
            title={t("opportunity:candidates.skillFilters.title")}
            setValues={handleFiltersChange("skills")}
            values={filters.skills || []}
            options={skillOptions}
            viewMoreLabels={t("opportunity:candidates.skillFilters.viewMore")}
            sorted={true}
            grouped={true}
            placeholder={t("opportunity:candidates.skillFilters.placeholder")}
          />
          <ExperienceFilters
            onChange={handleFiltersChange("workexperience")}
            loading={loading}
            value={filters.workexperience || []}
          />
          <ReferralFilter
            loading={loading}
            active={filters.referral}
            onChange={handleFiltersChange("referral")}
          />
          {/* <CheckboxFilters
            maxLength={6}
            loading={loading}
            title={t("opportunity:candidates.languageFilters.title")}
            setValues={handleFiltersChange("languages")}
            values={filters.languages || []}
            options={languageOptions}
            viewMoreLabels={t(
              "opportunity:candidates.languageFilters.viewMore"
            )}
            sorted={false}
            grouped
            placeholder={t(
              "opportunity:candidates.languageFilters.placeholder"
            )}
          /> */}
          <CheckboxFilters
            maxLength={4}
            loading={loading}
            title={t("opportunity:candidates.educationFilters.title")}
            setValues={handleFiltersChange("education")}
            values={filters.education || []}
            options={educationOptions}
            viewMoreLabels={t(
              "opportunity:candidates.educationFilters.viewMore"
            )}
            sorted={false}
            grouped={false}
            placeholder={t(
              "opportunity:candidates.educationFilters.placeholder"
            )}
          />
          <AgeFilters
            onChange={handleFiltersChange("agerange")}
            loading={loading}
            value={filters.agerange || []}
          />
          <AddressFilter
            loading={loading}
            setCountry={handleFiltersChange("country")}
            setProvince={handleFiltersChange("province")}
            setDistrict={handleFiltersChange("district")}
            country={filters.country || []}
            province={filters.province || []}
            district={filters.district || []}
          />
        </Sidebar>
        {loading ? (
          <SkeletonCandidates showGrid cardsNumber={6} />
        ) : isEmpty ? (
          <NonResults
            title={t("opportunity:noResults.noResultsCandidateBase")}
            messages={[
              t("opportunity:noResults.messageCandidateBaseReview"),
              t("opportunity:noResults.messageCandidateBaseRetry")
            ]}
          />
        ) : (
          <Wrapper
            filters={filters}
            filteredCandidates={filteredCandidates}
            handleFiltersChange={handleFiltersChange}
            mappedMetadata={mappedMetadata}
          >
            {({ data: candidates }) => (
              <Grid>
                {candidates &&
                  candidates.map(candidate => (
                    <CandidateCard
                      key={candidate.id}
                      candidate={candidate}
                      onSelectCandidate={() =>
                        history.push(
                          `/base-candidatos/candidato/${candidate.id}`
                        )
                      }
                      isSelected={Boolean(
                        R.find(
                          R.propEq("id", R.prop("id", candidate)),
                          multipleSelectedCandidates
                        )
                      )}
                      onSelect={
                        multipleSelectCandidate
                          ? selected =>
                              dispatch(
                                multipleSelectCandidate({
                                  candidate,
                                  selected
                                })
                              )
                          : undefined
                      }
                      actions={[
                        {
                          customRender: (
                            <WrapperAction key="share" colorHover="primary">
                              <Tooltip
                                title={t(
                                  "opportunity:candidates.multipleSelection.shareCandidateToOpp"
                                )}
                              >
                                <Button
                                  onClick={() => {
                                    setSelectedCandidate(candidate);
                                    setShareCandidateModalVisible(true);
                                  }}
                                  data-test="share-candidate-btn"
                                  key="share"
                                  type="primary"
                                  icon="export"
                                  shape="circle"
                                />
                              </Tooltip>
                            </WrapperAction>
                          )
                        }
                      ]}
                    />
                  ))}
              </Grid>
            )}
          </Wrapper>
        )}
        <ShareCandidatesModal
          onOk={
            !R.isEmpty(selectedCandidate)
              ? handleCandidateShare
              : handleBatchShare
          }
          onClose={() => setShareCandidateModalVisible(false)}
          visible={shareCandidateModalVisible}
        />
      </Content>
      <Route
        path={`/base-candidatos/candidato/:candidateId/:tab?`}
        render={({ match }) => {
          const candidateId = match.params.candidateId;
          const candidate = R.find(R.propEq("id", candidateId), candidates);
          const candidateIndex =
            candidate &&
            R.findIndex(({ id }) => id === candidateId, candidates);

          const onNext =
            candidate &&
            candidates[candidateIndex + 1] &&
            (() => {
              history.push(
                `/base-candidatos/candidato/${candidates[candidateIndex + 1].id}`
              );
            });

          const onPrev =
            candidate &&
            candidates[candidateIndex - 1] &&
            (() => {
              history.push(
                `/base-candidatos/candidato/${candidates[candidateIndex - 1].id}`
              );
            });

          if (!loading && !candidate) {
            notification.error({
              message: t("global:error"),
              description: t("toasts:candidates.fetchCandidateBaseRejected")
            });
            history.push(`/base-candidatos`);
          }
          return (
            <CandidatesBaseDetails
              onNext={onNext}
              onPrev={onPrev}
              onClose={() => {
                dispatch(SELECT_CANDIDATE.run({}));
                history.push(`/base-candidatos`);
              }}
            />
          );
        }}
      />
      <ActionsSnackbar
        candidates={candidates}
        actions={snackbarActions}
        actionsTitle={`${R.length(multipleSelectedCandidates)}`}
        actionsDescription={t(
          `opportunity:candidates.multipleSelection.${
            plurals ? "selectedCandidates" : "selectedCandidate"
          }`
        )}
        onClose={() => dispatch(resetMultipleSelectCandidates())}
        visible={!loading && R.not(R.isEmpty(multipleSelectedCandidates))}
        onSelectAll={handleSelectAllChange}
        selectAllStatus={selectAllStatus}
      />
    </Page>
  );
};

const enhancer = dispatchOnMount(() => DESELECT_OPPORTUNITY.run());

export default enhancer(CandidatesBase);
